// Loading.jsx
import { motion } from 'framer-motion';
import React from 'react';

const sizeMap = {
  sm: 16,
  md: 32,
  lg: 48,
  xl: 64
};

const textSizeMap = {
  sm: 12,
  md: 14,
  lg: 16,
  xl: 18
};

const LoadingSpinner = ({ size = 'md', color = 'var(--primary-600)', className = '' }) => {
  const px = sizeMap[size] || sizeMap.md;
  const border = Math.max(2, Math.round(px / 8));

  return (
    <motion.div
      aria-hidden
      style={{
        width: px,
        height: px,
        borderRadius: '9999px',
        borderStyle: 'solid',
        borderWidth: border,
        borderColor: 'var(--gray-200)',
        borderTopColor: color,
        boxSizing: 'border-box',
        display: 'inline-block'
      }}
      animate={{ rotate: 360 }}
      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
      className={className}
    />
  );
};

const LoadingContent = ({ size = 'md', text = '' }) => {
  const textSize = textSizeMap[size] || textSizeMap.md;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
      <LoadingSpinner size={size} />
      {text ? (
        <motion.p
          style={{ margin: 0, color: 'var(--gray-600)', fontWeight: 600, fontSize: textSize }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.16 }}
        >
          {text}
        </motion.p>
      ) : null}
    </div>
  );
};

const Loading = ({ size = 'md', text = '', overlay = false, className = '' }) => {
  if (overlay) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(255,255,255,0.92)',
          zIndex: 9999
        }}
        className={className}
      >
        <LoadingContent size={size} text={text} />
      </motion.div>
    );
  }

  return <LoadingContent size={size} text={text} />;
};

// ---------- Skeleton helpers (inline style versions) ----------
export const Skeleton = ({ width = '100%', height = 20, className = '', count = 1 }) => {
  const items = Array.from({ length: count });
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }} className={className}>
      {items.map((_, i) => (
        <motion.div
          key={i}
          style={{
            width,
            height,
            background: 'var(--gray-200, #e5e7eb)',
            borderRadius: 8,
            overflow: 'hidden'
          }}
          initial={{ opacity: 0.6 }}
          animate={{ opacity: [0.6, 1, 0.6] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut', delay: i * 0.12 }}
        />
      ))}
    </div>
  );
};

export const CardSkeleton = ({ className = '' }) => {
  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 12,
        boxShadow: '0 10px 30px rgba(2,6,23,0.04)',
        border: '1px solid #eef2f6',
        padding: 16
      }}
      className={className}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Skeleton height="20px" width="60%" />
        <Skeleton height="14px" width="100%" count={3} />
        <div style={{ display: 'flex', gap: 8 }}>
          <Skeleton height="32px" width="80px" />
          <Skeleton height="32px" width="100px" />
        </div>
      </div>
    </div>
  );
};

export const TableSkeleton = ({ rows = 5, columns = 4, className = '' }) => {
  const colTemplate = `repeat(${columns}, 1fr)`;
  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 12,
        boxShadow: '0 10px 30px rgba(2,6,23,0.04)',
        border: '1px solid #eef2f6',
        overflow: 'hidden'
      }}
      className={className}
    >
      {/* Header */}
      <div style={{ borderBottom: '1px solid #f3f6f9', background: 'var(--gray-50,#f9fafb)', padding: 12 }}>
        <div style={{ display: 'grid', gap: 12, gridTemplateColumns: colTemplate }}>
          {Array.from({ length: columns }).map((_, i) => (
            <Skeleton key={i} height="16px" width="80%" />
          ))}
        </div>
      </div>

      {/* Rows */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {Array.from({ length: rows }).map((_, r) => (
          <div key={r} style={{ padding: 12, borderBottom: '1px solid rgba(234,238,243,0.6)' }}>
            <div style={{ display: 'grid', gap: 12, gridTemplateColumns: colTemplate }}>
              {Array.from({ length: columns }).map((_, c) => (
                <Skeleton key={c} height="12px" width="90%" />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Loading;
